import type { AnchorPoint } from 'metalworks-domain'

import { DimensionIndicator } from '@salsita-ai/platform-project-sdk/configurator'

import { configuratorInterface } from '../systems/configurator-interface'

interface MeasurementIndicatorProps {
  anchorPointIdFrom: AnchorPoint['id']
  anchorPointIdTo: AnchorPoint['id']
}

export const MeasurementIndicator = (props: MeasurementIndicatorProps) => {
  const { anchorPointIdFrom, anchorPointIdTo } = props

  const anchorPointFrom = configuratorInterface.useEntityState(anchorPointIdFrom)
  const anchorPointTo = configuratorInterface.useEntityState(anchorPointIdTo)

  if (!anchorPointFrom || !anchorPointTo) {
    return null
  }

  const coordsFrom = anchorPointFrom.$absCoords3D
  const coordsTo = anchorPointTo.$absCoords3D

  const distanceX = Math.abs(coordsTo.x - coordsFrom.x)
  const distanceY = Math.abs(coordsTo.y - coordsFrom.y)
  const distanceZ = Math.abs(coordsTo.z - coordsFrom.z)

  const plate = anchorPointFrom.$plate
  const fontSize = anchorPointFrom.$plate.$indicatorLabelFontSize!

  // Plate is positioned in the middle. Calculate offset
  // to match "zero point" of the plate in 3D.
  const indicatorOffsetX = plate.$sizeX / 2
  const indicatorOffsetY = plate.$sizeY / 2

  const mainColor = plate.powderCoating ? '#FFFFFF' : '#000000'
  const contrastColor = plate.powderCoating ? '#000000' : '#FFFFFF'

  const commonProps: Partial<Parameters<typeof DimensionIndicator>[0]> = {
    labelFontSize: fontSize,
    labelColor: mainColor,
    labelOutlineColor: contrastColor,
    labelOutlineWidth: '2%',
    labelOutlineOpacity: 1,
    labelOutlineBlur: '1px',
    indicatorLineColor: mainColor,
    indicatorLineOutlineColor: contrastColor,
    indicatorLineOutlineWidth: 2,
    extrusionLineColor: mainColor,
    extrusionLineOutlineColor: contrastColor,
    extrusionLineOutlineWidth: 2,
    arrowHeadSize: 4,
    arrowHeadTypeStart: 'NONE',
    arrowHeadTypeEnd: 'CONE',
    arrowsMoveOutside: true,
    indicatorAlwaysVisible: true,
  }

  return (
    <>
      {distanceX > 0 && (
        <DimensionIndicator
          {...commonProps}
          key="measureX"
          startX={coordsFrom.x - indicatorOffsetX}
          startY={coordsFrom.y - indicatorOffsetY}
          startZ={coordsFrom.z}
          endX={coordsTo.x - indicatorOffsetX}
          endY={coordsFrom.y - indicatorOffsetY}
          endZ={coordsFrom.z}
          labelOffsetY={-fontSize}
        />
      )}

      {distanceY > 0 && (
        <DimensionIndicator
          {...commonProps}
          key="measureY"
          startX={coordsTo.x - indicatorOffsetX}
          startY={coordsFrom.y - indicatorOffsetY}
          startZ={coordsFrom.z}
          endX={coordsTo.x - indicatorOffsetX}
          endY={coordsTo.y - indicatorOffsetY}
          endZ={coordsFrom.z}
          labelAngle={-Math.PI / 2}
          labelOffsetX={fontSize}
        />
      )}

      {distanceZ > 0 && (
        <DimensionIndicator
          {...commonProps}
          key="measureZ"
          startX={coordsTo.x - indicatorOffsetX}
          startY={coordsTo.y - indicatorOffsetY}
          startZ={coordsFrom.z}
          endX={coordsTo.x - indicatorOffsetX}
          endY={coordsTo.y - indicatorOffsetY}
          endZ={coordsTo.z}
        />
      )}
    </>
  )
}
